import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { buttonClasses } from '@/design-system/Button'
import { Reveal } from '@/design-system/motion'
import { Container, Overline } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import type { PublicContentBlock, PublicEvent } from '../types'

/**
 * Le décor de la soirée, en pleine largeur.
 *
 * L'image porte déjà toute l'identité — logo, titre, édition, accroche. On ne
 * lui superpose donc aucun texte : elle se suffit, et la commenter reviendrait
 * à écrire deux fois la même chose.
 *
 * Le visuel vient de `events.hero_media_id` : il se remplace depuis
 * l'administration, sans toucher au code.
 */
export function DecorSection({
  event,
  text,
}: {
  event: PublicEvent
  /**
   * Le titre et la légende, réécrivables (bloc `site.decor` dans Contenus).
   * `undefined` : le bloc n'existe pas encore, textes de secours ; vides :
   * rien n'est affiché.
   */
  text?: PublicContentBlock | undefined
}) {
  const { t } = useTranslation()
  if (!event.hero_image_url) return null
  const legend = text ? text.body : t('decor.caption')
  const overline = text ? text.title : t('decor.overline')

  return (
    <section className="py-[var(--space-section)] sm:py-[var(--space-section-lg)]">
      <Container>
        <Reveal>
          {overline ? <Overline>{overline}</Overline> : null}
          <figure
            className={cn(
              'overflow-hidden rounded-lg border border-line-subtle',
              overline && 'mt-4',
            )}
          >
            <img
              src={event.hero_image_url}
              alt={t('decor.alt')}
              className="block w-full object-cover"
              loading="lazy"
            />
            {legend ? (
              <figcaption className="whitespace-pre-line border-t border-line-subtle bg-bg-elevated px-5 py-3 text-sm text-content-secondary">
                {legend}
              </figcaption>
            ) : null}
          </figure>
        </Reveal>
      </Container>
    </section>
  )
}

/**
 * Les deux promesses de la soirée : la table qui donne l'entrée, et le vote
 * qui appartient à la salle.
 *
 * Elles sont mises en avant plutôt que noyées dans la file éditoriale, parce
 * que ce sont les deux choses qui décident quelqu'un à venir. Leur texte reste
 * administrable : ce composant ne connaît que leurs clés.
 */
export function PromiseSection({
  table,
  jury,
}: {
  table: PublicContentBlock | undefined
  jury: PublicContentBlock | undefined
}) {
  const { t } = useTranslation()
  if (!table && !jury) return null

  return (
    <section className="py-[var(--space-section)] sm:py-[var(--space-section-lg)]">
      <Container>
        <div className="grid gap-4 lg:grid-cols-2">
          {table ? (
            <Reveal>
              <article className="edge-glow relative flex h-full flex-col rounded-lg border border-champagne-400/30 bg-bg-elevated p-7">
                <Overline>{table.subtitle}</Overline>
                <h2 className="mt-3 font-display text-heading-lg text-content-primary">
                  {table.title}
                </h2>
                <p className="mt-3 flex-1 text-body-lg text-content-secondary">{table.body}</p>
                <Link
                  to="/carte"
                  className={buttonClasses({ variant: 'outline', className: 'mt-6 self-start' })}
                >
                  {t('decor.bookTable')}
                </Link>
              </article>
            </Reveal>
          ) : null}

          {jury ? (
            <Reveal delay={80}>
              <article className="edge-glow relative flex h-full flex-col overflow-hidden rounded-lg border border-ember-400/30 bg-bg-elevated p-7">
                {/* Une braise diffuse derrière le texte : c'est la promesse la
                    plus chaleureuse de la page, elle mérite d'être éclairée. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(70%_60%_at_80%_0%,var(--ember-glow),transparent_70%)]"
                />
                <Overline>{jury.subtitle}</Overline>
                <h2 className="mt-3 font-display text-heading-lg text-content-primary">
                  {jury.title}
                </h2>
                <p className="mt-3 flex-1 text-body-lg text-content-secondary">{jury.body}</p>
                <Link
                  to="/concours"
                  className={buttonClasses({ variant: 'outline', className: 'mt-6 self-start' })}
                >
                  {t('decor.enterCompetition')}
                </Link>
              </article>
            </Reveal>
          ) : null}
        </div>
      </Container>
    </section>
  )
}
